import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import TableTab from "../components/tabs/table-tab";
import { ACCESS_TIME_PATTERNS } from "../constants/menu-types";
import { useLocale } from "../contexts/localeContext";
import { TableContextProvider } from "../contexts/table/TableContextProvider";
import { TranslationKeys } from "../contexts/TranslationKeys";
import { Category } from "../models/Category";
import columns from "../models/ColumnDefinitions/AccessTimePatternColumns";
import { Lookup } from "../models/Lookup";
import LookupTypes from "../models/LookupTypes";
import searchColumns from "../models/search/AccessTimePatternSearch";
import SiteSearch from "../models/search/SiteSearch";
import { DeleteEntityResult } from "../models/services/DeleteEntityResult";
import { deleteAccessTimePattern, getAccessTimePatterns } from "../services/accessTimePatternService";
import { getSiteNamesWithPagination } from '../services/siteService';
import TableContextStateMonitor from "../contexts/table/TableContextStateMonitor";
import { getDayNames } from "../utilities/DateUtils";

interface Props {
    setBusy: (isBusy: boolean) => void,
    categories: Category[],
    setCategories: (category: Category[]) => void,
    onFilterChange?: (filter: any) => void
}

const AccessTimePatternTab: FC<Props> = ({ setBusy, categories, setCategories, onFilterChange }) => {
    const { translate } = useLocale();
    const title = translate.byKey(TranslationKeys.accessTimePatterns);
    const [lookup, setLookup] = useState<Lookup[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const itemPath = '/access-control/enrolment/access-time-pattern/';

    useEffect(() => {
        (async () => {

            setLookup([{
                name: LookupTypes.site,
                values: [],
                dataMethod: getSiteNamesWithPagination,
                searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
            },
            {
                name: LookupTypes.days,
                values: getDayNames(translate)
            }]);
        })();
    }, [])

    const onDelete = (id: string): Promise<DeleteEntityResult> => {
        setBusy(true);
        return new Promise((resolve, reject) => {
            deleteAccessTimePattern(id)
                .then(
                    deleteResult => {
                        resolve(deleteResult);
                        setCategories([{ ...categories[0], count: (categories[0]?.count ?? 1) - 1 }]);
                        setBusy(false);
                    },
                    deleteResult => {
                        reject(deleteResult);
                        setBusy(false);
                    })
        });
    }

    // user preference items
    const userLoginId = useSelector((state: any) => (state.userInfo.id))
    const initialiseUserPreferences = (setDetails: (userLoginId, ACCESS_TIME_PATTERNS) => void) => {
        setDetails(userLoginId, ACCESS_TIME_PATTERNS);
    }
    // <-- end context

    return <TableContextProvider
        dataMethod={(listParameters) => getAccessTimePatterns(translate, listParameters)}
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        tableDescriptor={columns}
        searchDescriptor={searchColumns}
        useTable
        usePagination
        useFilter
        useSearch
        useSort
        deleteMethod={onDelete}
        lookup={lookup}
        customDelete={{ fieldName: "isDefault", comparisonValue: false }}>
        <TableContextStateMonitor onFilterChange={onFilterChange}/>
        <TableTab
            title={title}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            selectRows={false}
            rowPath={itemPath} 
            cyName="accessTimePattern"/>
    </TableContextProvider>;
};
export default AccessTimePatternTab;
